import { createContext, useContext, useRef, useState, useCallback, useEffect } from 'react';

const AudioCtx = createContext(null);

export function useBackgroundAudio() {
  return useContext(AudioCtx);
}

export function BackgroundAudioProvider({ children }) {
  const [playing, setPlaying] = useState(false);
  const [ready, setReady] = useState(false);
  const audioRef = useRef(null);
  const ctxRef = useRef(null);
  const masterRef = useRef(null);
  const birdTimerRef = useRef(null);
  const mutedRef = useRef(false);

  const scheduleBirds = useCallback(() => {
    const ac = ctxRef.current;
    const master = masterRef.current;
    if (!ac || ac.state === 'closed' || !master || mutedRef.current) return;

    const now = ac.currentTime;
    const count = 4 + Math.floor(Math.random() * 3);
    for (let i = 0; i < count; i++) {
      const t = now + 1 + Math.random() * 10;
      const freq = 1900 + Math.random() * 1000;
      const dur = 0.06 + Math.random() * 0.06;
      const o = ac.createOscillator();
      o.type = 'sine';
      o.frequency.setValueAtTime(freq, t);
      o.frequency.linearRampToValueAtTime(freq * 1.15, t + dur * 0.4);
      o.frequency.linearRampToValueAtTime(freq * 0.85, t + dur);
      const g = ac.createGain();
      g.gain.setValueAtTime(0, t);
      g.gain.linearRampToValueAtTime(0.02, t + 0.008);
      g.gain.exponentialRampToValueAtTime(0.001, t + dur + 0.05);
      o.connect(g).connect(master);
      o.start(t); o.stop(t + dur + 0.1);

      if (Math.random() < 0.35) {
        const t2 = t + dur + 0.04;
        const f2 = freq * 1.1;
        const d2 = 0.05 + Math.random() * 0.04;
        const o2 = ac.createOscillator();
        o2.type = 'sine';
        o2.frequency.setValueAtTime(f2, t2);
        o2.frequency.linearRampToValueAtTime(f2 * 1.1, t2 + d2 * 0.3);
        o2.frequency.linearRampToValueAtTime(f2 * 0.88, t2 + d2);
        const g2 = ac.createGain();
        g2.gain.setValueAtTime(0, t2);
        g2.gain.linearRampToValueAtTime(0.016, t2 + 0.006);
        g2.gain.exponentialRampToValueAtTime(0.001, t2 + d2 + 0.03);
        o2.connect(g2).connect(master);
        o2.start(t2); o2.stop(t2 + d2 + 0.05);
      }
    }
  }, []);

  const startAudio = useCallback(() => {
    if (audioRef.current || mutedRef.current) return;

    const audio = new Audio();
    audio.preload = 'auto';
    audio.loop = true;
    audio.volume = 0.6;
    audio.src = '/ambient-music.mp3';
    audioRef.current = audio;

    const tryPlay = () => {
      audio.play().then(() => {
        setPlaying(true);
        setReady(true);
      }).catch(() => {});
    };
    audio.addEventListener('canplay', tryPlay, { once: true });
    tryPlay();

    try {
      const ac = new (window.AudioContext || window.webkitAudioContext)();
      if (ac.state === 'suspended') ac.resume();
      ctxRef.current = ac;

      const master = ac.createGain();
      master.gain.value = 0.9;
      master.connect(ac.destination);
      masterRef.current = master;

      scheduleBirds();
      birdTimerRef.current = setInterval(scheduleBirds, 12000);
    } catch (e) {}
  }, [scheduleBirds]);

  const toggle = useCallback(() => {
    if (playing) {
      // MUTE
      mutedRef.current = true;
      if (audioRef.current) {
        audioRef.current.pause();
      }
      if (masterRef.current) {
        masterRef.current.gain.value = 0;
      }
      if (birdTimerRef.current) {
        clearInterval(birdTimerRef.current);
        birdTimerRef.current = null;
      }
      setPlaying(false);
    } else {
      // UNMUTE — if audio was never started, start it now
      mutedRef.current = false;
      if (!audioRef.current) {
        startAudio();
        return;
      }
      if (audioRef.current) {
        audioRef.current.volume = 0.6;
        audioRef.current.play().catch(() => {});
      }
      if (ctxRef.current && ctxRef.current.state === 'suspended') {
        ctxRef.current.resume();
      }
      if (masterRef.current) {
        masterRef.current.gain.value = 0.9;
      }
      // Restart bird scheduling
      if (!birdTimerRef.current) {
        scheduleBirds();
        birdTimerRef.current = setInterval(scheduleBirds, 12000);
      }
      setPlaying(true);
    }
  }, [playing, scheduleBirds, startAudio]);

  useEffect(() => {
    const handler = () => {
      if (!audioRef.current && !mutedRef.current) {
        startAudio();
        document.removeEventListener('click', handler);
        document.removeEventListener('touchstart', handler);
      }
    };
    document.addEventListener('click', handler);
    document.addEventListener('touchstart', handler);

    return () => {
      document.removeEventListener('click', handler);
      document.removeEventListener('touchstart', handler);
      if (birdTimerRef.current) clearInterval(birdTimerRef.current);
      if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
      if (ctxRef.current) ctxRef.current.close().catch(() => {});
    };
  }, [startAudio]);

  return (
    <AudioCtx.Provider value={{ isPlaying: playing, toggleMute: toggle, isInitialized: ready }}>
      {children}
    </AudioCtx.Provider>
  );
}
